import { convertTimeSlotsFromString } from "@clockwise/client-commons/src/constants/time-slot";
import { getByDayFormat } from "@clockwise/client-commons/src/util/format-time";
import {
  DayOfWeek,
  DayRangeFlexibilityType,
  EventConferenceType,
  EventPermissions,
  EventRecurrenceInput,
  RecurrenceIntervalTypeEnum,
  RepeatingEventSaveOption,
  ResponseStatusEnum,
  UpdateEventFlexibility,
  UpdateTimeOfDayTimeSlot,
} from "@clockwise/schema";
import { getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
import { orderBy } from "lodash";
import { DateTime } from "luxon";
import { ConferencingType } from "../../../__schema__";
import { SelectableRecurrenceOption, optionsList } from "../../event-card/molecules/ECRecurrence";
import { EventCardAttendee, EventCardFlex, SelectedWorkdayOption } from "../../event-card/types";
import { calculateIsAllDay } from "../../event-card/utils/eventCardTime.util";
import { InputForSaveEventChanges } from "./useEditExistingEvent";

export const formatAttendeesToSaveToEvent = (
  attendeeOrgPeople: EventCardAttendee[],
  currentUserResponseStatus: ResponseStatusEnum,
) => {
  return attendeeOrgPeople.map((attendee) => {
    const responseStatus = attendee.isYou ? currentUserResponseStatus : attendee.attendingStatus;
    return {
      isOrganizer: Boolean(attendee.isOrganizer),
      responseStatus: responseStatus || ResponseStatusEnum.NotApplicable,
      calendarId: attendee.primaryCalendar,
      isOptional: attendee.isOptional, //this is nullable on the input and in the BE api call
    };
  });
};

export const formatVCToSave = (
  videoType: ConferencingType | undefined,
  originalVideoType: ConferencingType | undefined,
): EventConferenceType | undefined => {
  if (videoType === originalVideoType) {
    return undefined;
  }

  if (!videoType) return EventConferenceType.Removed; // If there is no longer a videoType, we set to "Removed"

  switch (videoType) {
    case ConferencingType.MEET:
      return EventConferenceType.Meet;
    case ConferencingType.TEAMS_FOR_BUSINESS:
      return EventConferenceType.TeamsForBusiness;
    case ConferencingType.ZOOM:
      return EventConferenceType.Zoom;
    default:
      // We don't cover saving other values (yet)
      return undefined;
  }
};

export const isStandardRecurrence = (
  recurrence: string,
): recurrence is SelectableRecurrenceOption => {
  return optionsList.includes(recurrence as SelectableRecurrenceOption);
};

export const formatSparkleInTitle = (eventName: string, isFlexble: boolean) => {
  const alreadyHasSparkle = eventName.startsWith("❇️");
  if (isFlexble) {
    if (!alreadyHasSparkle) {
      return `❇️ ${eventName}`;
    }
  } else {
    if (alreadyHasSparkle) {
      return eventName.replace("❇️ ", "");
    }
  }
  return eventName;
};

export const formatRecurrenceToString = (
  recurrence:
    | {
        intervalCount: number;
        intervalType: RecurrenceIntervalTypeEnum;
        recurrenceString: string;
      }
    | undefined
    | null,
): SelectableRecurrenceOption | string => {
  if (!recurrence || recurrence?.intervalCount === 0) return "None";
  if (recurrence.recurrenceString) return recurrence.recurrenceString;
  // After this point, its either a CW created recurrence from proposals or no recurrence
  return formatRecurrenceToSelectableOption(recurrence);
};

export const formatRecurrenceToSelectableOption = ({
  intervalCount,
  intervalType,
  supportCustomRecurrence,
}: {
  intervalCount: number;
  intervalType: RecurrenceIntervalTypeEnum;
  supportCustomRecurrence?: boolean;
}): SelectableRecurrenceOption | "Custom" => {
  if (intervalType === RecurrenceIntervalTypeEnum.WEEKLY && intervalCount === 1) {
    return "Weekly";
  }
  if (intervalType === RecurrenceIntervalTypeEnum.DAILY && intervalCount === 1) {
    return "Weekdays";
  }
  if (intervalType === RecurrenceIntervalTypeEnum.MONTHLY && intervalCount === 1) {
    return "Monthly";
  }
  if (intervalType === RecurrenceIntervalTypeEnum.WEEKLY && intervalCount === 2) {
    return "Biweekly";
  }
  return supportCustomRecurrence ? "Custom" : "None";
};

const convertStringToDayOfWeekFlexibility = (range: "Day" | "Week" | null) => {
  switch (range) {
    case "Day":
      return DayRangeFlexibilityType.Day;
    case "Week":
      return DayRangeFlexibilityType.Week;
    default:
      return DayRangeFlexibilityType.DayOfWeek;
  }
};

export const convertStringToDayOfWeek = (day: SelectedWorkdayOption) => {
  switch (day) {
    case "Monday":
      return DayOfWeek.Monday;
    case "Tuesday":
      return DayOfWeek.Tuesday;
    case "Wednesday":
      return DayOfWeek.Wednesday;
    case "Thursday":
      return DayOfWeek.Thursday;
    case "Friday":
      return DayOfWeek.Friday;
    case "Saturday":
      return DayOfWeek.Saturday;
    case "Sunday":
      return DayOfWeek.Sunday;
    default:
      return DayOfWeek.Monday;
  }
};

export const formatFlexUpdates = (
  flexDetails: EventCardFlex,
  eventId: string,
): UpdateEventFlexibility => {
  const range = convertStringToDayOfWeekFlexibility(flexDetails.timeRangeFlexibility?.range);
  const startTime = convertTimeSlotsFromString(flexDetails.timeOfDayFlexibility.startTime, true);
  const endTime = convertTimeSlotsFromString(flexDetails.timeOfDayFlexibility.endTime, false);
  const body: UpdateEventFlexibility = {
    eventId,
    dayRangeFlexibility: {
      type: range,
      allowedDays:
        range === DayRangeFlexibilityType.DayOfWeek
          ? flexDetails.timeRangeFlexibility.daysOfWeekFlexibility.map(convertStringToDayOfWeek)
          : null,
    },
  };
  if (startTime && endTime) {
    body.timeOfDayFlexibility = {
      allowedStart: startTime as UpdateTimeOfDayTimeSlot,
      allowedEnd: endTime as UpdateTimeOfDayTimeSlot,
    };
  }
  return body;
};

export const formatRecurrenceToSave = (
  recurrence: SelectableRecurrenceOption | string,
  startISO: DateTime,
): EventRecurrenceInput | null => {
  const timeZone = getRenderTimeZone();

  if (isStandardRecurrence(recurrence)) {
    switch (recurrence) {
      case "None":
        return {
          intervalType: RecurrenceIntervalTypeEnum.NONE,
          intervalCount: 0,
          eventStartTime: startISO,
          timeZone: timeZone,
          byDay: "",
        };
      case "Weekdays":
        return {
          intervalType: RecurrenceIntervalTypeEnum.WEEKLY, // "Daily on weekdays" is really weekly
          intervalCount: 1,
          eventStartTime: startISO,
          timeZone: timeZone,
          byDay: "MO,TU,WE,TH,FR",
        };
      case "Weekly":
        return {
          intervalType: RecurrenceIntervalTypeEnum.WEEKLY,
          intervalCount: 1,
          eventStartTime: startISO,
          timeZone: timeZone,
          byDay: getByDayFormat(startISO),
        };
      case "Biweekly":
        return {
          intervalType: RecurrenceIntervalTypeEnum.WEEKLY,
          intervalCount: 2,
          eventStartTime: startISO,
          timeZone: timeZone,
          byDay: getByDayFormat(startISO),
        };
      case "Monthly":
        return {
          intervalType: RecurrenceIntervalTypeEnum.MONTHLY,
          intervalCount: 1,
          eventStartTime: startISO,
          timeZone: timeZone,
          byDay: getByDayFormat(startISO, true),
        };
      default:
        return null;
    }
  }
  return null;
};

export const getBodyForSaveChanges = (
  body: InputForSaveEventChanges,
  permissions: EventPermissions,
) => {
  let eventFields = {};
  switch (permissions) {
    case EventPermissions.ISATTENDEE:
      eventFields = {
        responseStatus: body.responseStatus,
      };
      break;
    case EventPermissions.ALL:
    case EventPermissions.MODIFY:
      eventFields = body;
      break;
    case EventPermissions.INVITE:
      eventFields = {
        responseStatus: body.responseStatus,
        attendees: body.attendees,
      };
      break;
  }
  return {
    ...eventFields,
    organizerCalendarId: body.organizerCalendarId,
    sendNotifications: body.sendNotifications,
    repeatingEventSaveOption: body.repeatingEventSaveOption,
  };
};

const ATTENDEESTATUS_ORDER = {
  Accepted: 1,
  Declined: 2,
  Tentative: 3,
  NeedsAction: 4,
  NotApplicable: 5,
};

export const sortAttendees = (attendees: EventCardAttendee[]) => {
  return orderBy(
    attendees,
    [
      "isOrganizer",
      (attendee) => {
        const statusAsString = attendee.attendingStatus?.toString() || "NotApplicable";
        return ATTENDEESTATUS_ORDER[statusAsString as keyof typeof ATTENDEESTATUS_ORDER];
      },
      "profile.givenName",
    ],
    ["desc", "asc", "asc"],
  );
};

export const getDisabledSaveOptions = ({
  hasFlexBeenModified,
  hasRecurrenceBeenModified,
  isDescriptionOmitted,
}: {
  hasFlexBeenModified: boolean;
  hasRecurrenceBeenModified: boolean;
  isDescriptionOmitted: boolean;
}) => {
  const disabledSaveOptions: RepeatingEventSaveOption[] = [];
  if (isDescriptionOmitted) {
    disabledSaveOptions.push(RepeatingEventSaveOption.ThisAndFutureInstances);
  }
  if (hasFlexBeenModified || hasRecurrenceBeenModified) {
    disabledSaveOptions.push(RepeatingEventSaveOption.ThisInstanceOnly);
  }
  return disabledSaveOptions;
};

export const isAllDayBasedOnTimes = (startISO?: DateTime, endISO?: DateTime) => {
  if (!startISO || !endISO) return false;
  return calculateIsAllDay(startISO, endISO);
};
